import {copyText} from '@github-ui/copy-to-clipboard'
import {sendEvent} from '@github-ui/hydro-analytics'
import {CheckIcon, LockIcon, UnlockIcon} from '@primer/octicons-react'
import {Button, Dialog, FormControl, Spinner, Stack, Text, TextInput} from '@primer/react'
import type React from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useUpdateTaskSharingStatus} from '../hooks/use-update-task-sharing-status'
import {generateTaskPathForRepo} from '../utils/generate-task-path'
import {useIntentDetectionEnabled} from '../contexts/IntentDetectionEnabledContext'
import styles from './TaskSharingDialog.module.css'

interface TaskSharingDialogProps {
  taskId: string
  repoOwner?: string
  repoName?: string
  isShared: boolean
  closeDialog: () => void
  returnFocusRef?: React.RefObject<HTMLElement | null>
}

export function TaskSharingDialog({
  taskId,
  repoOwner,
  repoName,
  isShared: initialIsShared,
  closeDialog,
  returnFocusRef,
}: TaskSharingDialogProps) {
  const [isShared, setIsShared] = useState(initialIsShared)
  const [prevInitialIsShared, setPrevInitialIsShared] = useState(initialIsShared)
  if (initialIsShared !== prevInitialIsShared) {
    setPrevInitialIsShared(initialIsShared)
    setIsShared(initialIsShared)
  }
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const linkInputRef = useRef<HTMLInputElement>(null)
  const {isUsingIntentDetection} = useIntentDetectionEnabled()

  const {mutate: updateSharingStatus, isPending} = useUpdateTaskSharingStatus({
    owner: repoOwner,
    repo: repoName,
    taskId,
  })

  const taskPath = generateTaskPathForRepo({
    repositoryNwo: repoOwner && repoName ? `${repoOwner}/${repoName}` : undefined,
    taskId,
    isUsingIntentDetection,
  })

  const fullUrl = useMemo(() => {
    if (typeof window === 'undefined') return taskPath
    return `${window.location.origin}${taskPath}`
  }, [taskPath])

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 1500)
      return () => clearTimeout(timer)
    }
  }, [copied])

  const handleShare = useCallback(() => {
    setError(null)
    updateSharingStatus(
      {sharing_status: 'shared'},
      {
        onSuccess: () => {
          setIsShared(true)
          sendEvent('agent_sessions.task_actions', {
            target: 'SHARE_SESSION',
            task_id: taskId,
          })
        },
        onError: () => {
          setError('Failed to share session. Please try again.')
        },
      },
    )
  }, [updateSharingStatus, taskId])

  const handleUnshare = useCallback(() => {
    setError(null)
    updateSharingStatus(
      {sharing_status: 'unshared'},
      {
        onSuccess: () => {
          setIsShared(false)
          sendEvent('agent_sessions.task_actions', {
            target: 'UNSHARE_SESSION',
            task_id: taskId,
          })
        },
        onError: () => {
          setError('Failed to unshare session. Please try again.')
        },
      },
    )
  }, [updateSharingStatus, taskId])

  const handleCopyLink = useCallback(async () => {
    try {
      await copyText(fullUrl)
      setCopied(true)
      sendEvent('agent_sessions.task_actions', {
        target: 'COPY_SHARED_SESSION_LINK',
        task_id: taskId,
      })
    } catch {
      linkInputRef.current?.select()
    }
  }, [fullUrl, taskId])

  if (isShared) {
    return (
      <Dialog title="Session shared" onClose={closeDialog} width="large" returnFocusRef={returnFocusRef}>
        <div className={styles.container}>
          <Stack direction="vertical" gap="condensed">
            <Stack direction="horizontal" padding="none" align="center" justify="start" gap="condensed">
              <span>This session is visible to anyone with access to this repository who has the link.</span>
            </Stack>

            <div className={styles.sharedLinkContainer}>
              <FormControl id="share-link" className={styles.textInputContainer}>
                <FormControl.Label>Share link</FormControl.Label>
                <TextInput ref={linkInputRef} size="large" readOnly value={fullUrl} className={styles.textInput} />
              </FormControl>
            </div>
          </Stack>

          <Stack
            direction="horizontal"
            gap="condensed"
            align="center"
            justify="space-between"
            className={styles.buttonStack}
          >
            <div role="status">
              {error ? (
                <Text size="medium" className={styles.error}>
                  {error}
                </Text>
              ) : isPending ? (
                <div className={styles.quickFadeUp}>
                  <Spinner size="small" className={styles.spinner} />
                  Unsharing session...
                </div>
              ) : copied ? (
                <div className={styles.quickFadeUp}>
                  <CheckIcon className={styles.successIcon} />
                  <Text size="medium" className={styles.success}>
                    Copied to clipboard
                  </Text>
                </div>
              ) : (
                <div>
                  <UnlockIcon className={styles.lockIcon} />
                  <Text size="medium" className={styles.mutedText}>
                    Visible to anyone with access to this repository
                  </Text>
                </div>
              )}
            </div>
            <Stack direction="horizontal" gap="condensed" align="center" justify="end">
              <Button variant="danger" disabled={isPending} onClick={handleUnshare}>
                Unshare
              </Button>
              <Button variant="primary" onClick={handleCopyLink}>
                Copy link
              </Button>
            </Stack>
          </Stack>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog title="Share session" onClose={closeDialog} width="large" returnFocusRef={returnFocusRef}>
      <div className={styles.container}>
        <Stack direction="vertical" gap="condensed">
          <Stack direction="horizontal" padding="none" align="center" justify="start" gap="condensed">
            <span>
              When shared, this session and future messages will be visible to anyone with repository access as view
              only.
            </span>
          </Stack>
        </Stack>

        <Stack
          direction="horizontal"
          gap="condensed"
          align="center"
          justify="space-between"
          className={styles.buttonStack}
        >
          <div role="status">
            {error ? (
              <Text size="medium" className={styles.error}>
                {error}
              </Text>
            ) : isPending ? (
              <div className={styles.quickFadeUp}>
                <Spinner size="small" className={styles.spinner} />
                Sharing session...
              </div>
            ) : (
              <div>
                <LockIcon className={styles.lockIcon} />
                <Text size="medium" className={styles.mutedText}>
                  This session is private
                </Text>
              </div>
            )}
          </div>
          <Stack direction="horizontal" gap="condensed" align="center" justify="end">
            <Button onClick={closeDialog}>Cancel</Button>
            <Button variant="primary" disabled={isPending} onClick={handleShare}>
              Share
            </Button>
          </Stack>
        </Stack>
      </div>
    </Dialog>
  )
}
